/**
 * Calendar Integration Service
 *
 * Wires up the three deployed Cloud Functions for Google Calendar OAuth.
 * Other functions (getBusyTimes, createBookingEvent, etc.) remain stubbed
 * for Phase 2 implementation.
 */

import { db } from '../config/firebase-config';
import { doc, getDoc } from 'firebase/firestore';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';

// ---------------------------------------------------------------------------
// Initiate Google OAuth flow
// Calls the Cloud Function to get an auth URL, then redirects the browser.
// ---------------------------------------------------------------------------
export const initiateCalendarConnect = async (uid) => {
  const url = `${FUNCTIONS_BASE}/initiateGoogleCalendarAuth?uid=${encodeURIComponent(uid)}`;
  const response = await fetch(url);
  const data = await response.json();
  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error(data.error || 'Failed to get OAuth URL');
  }
};

// ---------------------------------------------------------------------------
// Disconnect Google Calendar
// POSTs to the Cloud Function which revokes the token and cleans up Firestore.
// ---------------------------------------------------------------------------
export const disconnectCalendar = async (uid) => {
  const response = await fetch(`${FUNCTIONS_BASE}/disconnectGoogleCalendar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uid }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to disconnect calendar');
  }
  return await response.json();
};

// ---------------------------------------------------------------------------
// Get calendar connection status from the main practitioner document.
//
// The sensitive tokens live in the integrations subcollection (Admin SDK only).
// The frontend only needs the connected flag + timestamp, which the Cloud
// Functions write to the top-level practitioners/{uid} doc on every
// connect/disconnect — avoiding subcollection permission issues entirely.
// ---------------------------------------------------------------------------
export const getCalendarStatus = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = docSnap.data();
    if (!data.calendarConnected) return null;
    return {
      connected: true,
      provider: data.calendarProvider || 'google',
      connectedAt: data.calendarConnectedAt?.toDate?.() || null,
    };
  } catch (err) {
    console.warn('[Calendar] Error reading status from practitioner doc:', err.message);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Legacy export — kept for backward compatibility with existing import
// ---------------------------------------------------------------------------
export const connectGoogleCalendar = initiateCalendarConnect;

// ---------------------------------------------------------------------------
// Phase 2 stubs — not yet implemented
// ---------------------------------------------------------------------------
export const checkCalendarConnection = async (userId) => ({
  connected: false,
  provider: null,
  email: null,
  lastSynced: null,
});

export const getBusyTimes = async (userId, startDate, endDate) => [];

export const createBookingEvent = async (userId, bookingDetails) => ({
  success: false,
  eventId: null,
});

export const updateBookingEvent = async (userId, eventId, bookingDetails) => ({
  success: false,
});

export const deleteBookingEvent = async (userId, eventId) => ({
  success: false,
});

const calendarService = {
  initiateCalendarConnect,
  disconnectCalendar,
  getCalendarStatus,
  connectGoogleCalendar,
  checkCalendarConnection,
  getBusyTimes,
  createBookingEvent,
  updateBookingEvent,
  deleteBookingEvent,
};

export default calendarService;
